import { ApiProvider } from '@/provider/ApiProvider';
import {
  type ContextType,
  getActiveAssignment,
  getActiveContextType,
  legacyUserType,
} from '@/auth/activeAssignment';
import { getHeaderPropertyId } from '@/selection/headerSelection';

/**
 * Every "what kind of user is this" decision of the UI (part 7, conversion
 * rule 5). The answer is the ACTIVE role assignment's context type (INTERNAL,
 * BRAND, RIGHT_HOLDER, AGENCY; the previewed role's while previewing), never
 * `users.type`, role names or role-type names. Without an active assignment
 * every helper answers false: no context-specific UI (the servers decide and
 * refuse anyway).
 *
 * The UI only HIDES things with these helpers; every rule that limits data
 * (scope, agency right-seller / DQS sign-off, ...) is decided by the server.
 */

export type { ContextType };

/** The active context type, or null without an active assignment. */
export const activeContextType = (): ContextType | null => getActiveContextType();

export const isInternalContext = (): boolean => activeContextType() === 'INTERNAL';
export const isBrandContext = (): boolean => activeContextType() === 'BRAND';
export const isRightHolderContext = (): boolean => activeContextType() === 'RIGHT_HOLDER';
export const isAgencyContext = (): boolean => activeContextType() === 'AGENCY';

/**
 * Right holder or agency: the contexts that work from a partner (header
 * partner switcher, `property_id` as the page context of create forms).
 */
export const isPartnerSideContext = (): boolean => isRightHolderContext() || isAgencyContext();

/** A customer context (brand, right holder, agency): the tenant's own theme, templates, ... */
export const isCustomerContext = (): boolean =>
  isBrandContext() || isRightHolderContext() || isAgencyContext();

/** Any active context (a valid assignment is in use). */
export const hasActiveContext = (): boolean => activeContextType() !== null;

/**
 * The active context type in the legacy codes (1 internal, 2 brand, 3 right
 * holder, 4 agency), 0 without an active assignment. For code that still
 * compares numbers; new code uses the named helpers above.
 */
export const getUserType = (): number => legacyUserType(activeContextType()) ?? 0;

/** The brand / partner the active context belongs to (its organisation), if any. */
export const activeContextBrandId = (): number | null => getActiveAssignment()?.brandId ?? null;
export const activeContextPropertyId = (): number | null =>
  getActiveAssignment()?.propertyId ?? null;

/** A named permission of the active assignment (User/PermissionsMap narrowed to it). */
export const can = (permission: string): boolean => Boolean(ApiProvider.permission(permission));

/**
 * Comments (was: role-id substring checks on the stored `userRole` list, ids 1 /
 * 6 / 11 = internal roles): an internal context with `comments_edit` may edit
 * other users' comments; internal contexts must tag their comments. UI only:
 * the comment service decides the write.
 */
export const canEditOthersComments = (): boolean => isInternalContext() && can('comments_edit');
export const commentTagsRequired = (): boolean => isInternalContext();

/** Header partner switcher: right-holder and agency contexts (§13.4, H2). */
export const shouldShowPropertySelector = (): boolean => isPartnerSideContext();

/** Header brand switcher: every context; it lists only in-scope brands (§13.4, H1). */
export const shouldShowBrandSelector = (): boolean => hasActiveContext();

export const getStoredPropertyId = () => getHeaderPropertyId() || null;

export const resolveRightHolderPropertyId = (propertyId?: string | number | null) => {
  if (propertyId != null && propertyId !== '') {
    return String(propertyId);
  }

  return getStoredPropertyId();
};

/** Brand portfolio: the partner details block for partner-side contexts with a partner chosen. */
export const shouldShowBrandPortfolioPartnerDetails = (propertyId?: string | number | null) =>
  isPartnerSideContext() && Boolean(resolveRightHolderPropertyId(propertyId));
